import React, { useState, useEffect } from 'react';
import { Form, Input, Button } from 'antd';
import EditableDrawer from 'components/EditableDrawer';
import ZipcodeInput from 'components/ZipcodeInput';
import { usePickupRouteById } from '../hooks/usePickupRouteById';

interface ViewRouteDrawerProps {
  visible: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  routeId: number;
  // 添加完整的车辆数据
  vehicles: Array<{
    id: number;
    vehicleNumber: string;
    licensePlate: string;
    status: number;
    vehicleModelId: number;
    vehicleModelName: string;
  }>;
  // 添加司机数据
  drivers: Array<{
    accountId: string;
    driverName: string;
    status: number;
  }>;
  disableEdit?: boolean;
  disableDelete?: boolean;
}

const ViewRouteDrawer: React.FC<ViewRouteDrawerProps> = ({
  visible,
  onClose,
  onEdit,
  onDelete,
  routeId,
  vehicles,
  drivers,
  disableEdit = false,
  disableDelete = false,
}) => {
  const [form] = Form.useForm();
  const [zipcodesData, setZipcodesData] = useState<string[]>([]);

  // 从数据库获取最新数据
  const { route: latestRoute, loading: routeLoading } = usePickupRouteById(routeId);

  // 当从数据库获取到最新数据时，更新表单
  useEffect(() => {
    if (latestRoute && visible) {
      try {
        const zipcodesArray = JSON.parse(latestRoute.zipcodesListJson);
        const formattedZipcodes = Array.isArray(zipcodesArray) ? zipcodesArray.join(', ') : latestRoute.zipcodesListJson;
        setZipcodesData(Array.isArray(zipcodesArray) ? zipcodesArray : []);
        
        // 获取司机名称
        let driverName = '-';
        if (latestRoute.driverId) {
          const selectedDriver = drivers.find(d => d.accountId === latestRoute.driverId.toString());
          if (selectedDriver?.driverName) {
            driverName = selectedDriver.driverName;
          }
        }
        
        // 获取车辆名称
        let vehicleName = '-';
        if (latestRoute.vehicleId) {
          const selectedVehicle = vehicles.find(v => v.id === latestRoute.vehicleId);
          if (selectedVehicle) {
            vehicleName = `${selectedVehicle.vehicleNumber}-${selectedVehicle.licensePlate}`;
          }
        }
        
        const vehicleModelName = latestRoute.vehicleTypeName || '-';
        
        form.setFieldsValue({
          id: latestRoute.id.toString(),
          route: latestRoute.routeName,
          zipcodes: formattedZipcodes,
          driverId: driverName,
          vehicleId: vehicleName,
          vehicleModelId: vehicleModelName
        });
      } catch (error) {
        console.error('Error parsing zipcodes from latest route:', error);
      }
    }
  }, [latestRoute, visible, form, vehicles, drivers]);

  if (routeLoading) {
    return (
      <EditableDrawer
        title="Route Details"
        visible={visible}
        onClose={onClose}
        width={550}
      >
        <div className="text-center py-8">Loading route data...</div>
      </EditableDrawer>
    );
  }

  return (
    <EditableDrawer
      title="Route Details"
      visible={visible}
      onClose={onClose}
      footer={
        <div className="flex justify-end space-x-4">
          <Button onClick={onEdit} type="primary" disabled={disableEdit}>
            Edit
          </Button>
          <Button onClick={onDelete} danger disabled={disableDelete}>
            Delete
          </Button>
        </div>
      }
      width={550}
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
      >
        <Form.Item name="id" label="ID">
          <Input disabled />
        </Form.Item>

        <Form.Item name="route" label="Route Name">
          <Input disabled />
        </Form.Item>

        <Form.Item name="zipcodes" label="Zipcodes">
          <ZipcodeInput
            data={{ zipcodes: zipcodesData }}
            isEditing={false}
            rows={4}
          />
        </Form.Item>

        <Form.Item name="driverId" label="Driver">
          <Input disabled />
        </Form.Item>

        <Form.Item name="vehicleId" label="Vehicle">
          <Input disabled />
        </Form.Item>

        <Form.Item name="vehicleModelId" label="Vehicle Model">
          <Input disabled />
        </Form.Item>
      </Form>
    </EditableDrawer>
  );
};

export default ViewRouteDrawer;
